import {
  FEATURE_FLAGS_ALL_OFF,
  FeatureFlagsResponse,
  type FeatureFlag,
  type FeatureFlags,
} from '@uit/shared/schemas/feature-flags';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Feature flags (T-152). Fetches /api/feature-flags once at app load and exposes
 * them app-wide. Wraps BOTH the authed admin shell and the unauthenticated
 * magic-link onboarding form, so gated UI is hidden everywhere until a flag is
 * on. Until the fetch resolves, every flag reads false (safe default — a gated
 * feature never flashes visible before we know it's off).
 */
export interface FeatureFlagsContextValue {
  flags: FeatureFlags;
  loading: boolean;
  refetch: () => Promise<void>;
}

// Default to all-off (no provider → gated features hidden) rather than throwing.
// Fail-safe and matches the server's "missing key = OFF"; the real provider is
// mounted at the app root, so in-app consumers always get live values.
const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: FEATURE_FLAGS_ALL_OFF,
  loading: false,
  refetch: async () => {},
});

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlags>(FEATURE_FLAGS_ALL_OFF);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await fetch('/api/feature-flags', { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      setFlags(FeatureFlagsResponse.parse(await res.json()));
    } catch {
      // On any error keep the safe all-off default rather than guessing.
      setFlags(FEATURE_FLAGS_ALL_OFF);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, refetch }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  return useContext(FeatureFlagsContext);
}

/** True iff the named feature is on. False while flags are still loading. */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  return useFeatureFlags().flags[flag];
}
